import React, { ReactElement } from 'react';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import CloseIcon from '@mui/icons-material/Close';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Chip,
  Typography,
  Alert,
} from '@mui/material';

export type PasteTarget = {
  rowNumber?: number;
  rowId?: any;
  label?: string;
  oldValue?: any;
  newValue?: any;
  status: 'ok' | 'skip-nonrow' | 'skip-empty' | 'type-mismatch' | 'no-row';
  rawValue?: string;
  // belső, az alkalmazáshoz (a dialog nem jeleníti meg)
  row?: any;
  field?: string;
};

export type PastePreviewData = {
  columnName: string;
  labelColumnName: string;
  targets: PasteTarget[];
};

type Props = {
  data: PastePreviewData;
  loading?: boolean;
  onConfirm: () => void;
  onCancel: () => void;
};

const STATUS_LABEL: Record<PasteTarget['status'], string> = {
  ok: 'Beillesztésre kerül',
  'skip-nonrow': 'Kihagyva (nem adat sor)',
  'skip-empty': 'Kihagyva (üres érték)',
  'type-mismatch': 'Kihagyva (típus nem megfelelő)',
  'no-row': 'Kihagyva (nincs több sor)',
};

const STATUS_COLOR: Record<
  PasteTarget['status'],
  'success' | 'default' | 'warning'
> = {
  ok: 'success',
  'skip-nonrow': 'default',
  'skip-empty': 'default',
  'type-mismatch': 'warning',
  'no-row': 'default',
};

const renderCellValue = (value: any) => {
  if (value === null || value === undefined || value === '') return '—';
  if (typeof value === 'object') {
    return value?.name ?? JSON.stringify(value);
  }
  return String(value);
};

export const PastePreviewDialog = ({
  data,
  loading,
  onConfirm,
  onCancel,
}: Props): ReactElement => {
  const validCount = data.targets.filter((t) => t.status === 'ok').length;
  const skippedCount = data.targets.length - validCount;

  return (
    <Dialog open={true} onClose={onCancel} fullWidth maxWidth="md">
      <DialogTitle sx={{ m: 0, p: 2 }}>
        Beillesztés megerősítése – oszlop: <b>{data.columnName}</b>
      </DialogTitle>
      <IconButton
        aria-label="close"
        onClick={onCancel}
        sx={{
          position: 'absolute',
          right: 8,
          top: 8,
          color: (theme) => theme.palette.grey[500],
        }}
      >
        <CloseIcon />
      </IconButton>

      <DialogContent dividers>
        <Alert severity="info" sx={{ mb: 2 }}>
          {validCount} sorba történik beillesztés
          {skippedCount ? `, ${skippedCount} sor kihagyva` : ''}. A művelet csak
          a „Beillesztés” gombra hajtódik végre.
        </Alert>

        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>#</TableCell>
              <TableCell>ID</TableCell>
              <TableCell>{data.labelColumnName}</TableCell>
              <TableCell>Jelenlegi</TableCell>
              <TableCell>Új érték</TableCell>
              <TableCell>Státusz</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {data.targets.map((t, index) => {
              const isOk = t.status === 'ok';
              return (
                <TableRow
                  key={index}
                  sx={{ opacity: isOk ? 1 : 0.55 }}
                >
                  <TableCell>{t.rowNumber ?? '—'}</TableCell>
                  <TableCell>{t.rowId ?? '—'}</TableCell>
                  <TableCell>
                    <span
                      dangerouslySetInnerHTML={{
                        __html: t.label ? String(t.label) : '—',
                      }}
                    />
                  </TableCell>
                  <TableCell>{renderCellValue(t.oldValue)}</TableCell>
                  <TableCell>
                    <b>{renderCellValue(isOk ? t.newValue : t.rawValue)}</b>
                  </TableCell>
                  <TableCell>
                    <Chip
                      size="small"
                      label={STATUS_LABEL[t.status]}
                      color={STATUS_COLOR[t.status]}
                    />
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>

        {!validCount && (
          <Typography variant="body2" sx={{ mt: 2, color: 'error.main' }}>
            Nincs érvényes cél-cella, nincs mit beilleszteni.
          </Typography>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3 }}>
        <Button onClick={onCancel} color="inherit" disabled={loading}>
          Mégse
        </Button>
        <Button
          onClick={onConfirm}
          variant="contained"
          disabled={!validCount || loading}
        >
          Beillesztés ({validCount})
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default PastePreviewDialog;
